import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardFooter,
    CardHeader,
} from "@/components/ui/card";
import { TreatmentNameToInitialQuestionnaireIDMapping } from "@/constants/Mappings";
import { Treatment } from "@/constants/treatments";
import { cn } from "@/lib/utils";
import { PillBGMapping, PillMapping } from "@/utils/discover-mapping";
import { ArrowRight } from "lucide-react";

import TrackedLink from "./TrackedLink";

export function DesktopTreatmentCard({
    treatment,
    index,
}: {
    treatment: Treatment;
    index: number;
}) {
    const initialQuestionnaireId =
        TreatmentNameToInitialQuestionnaireIDMapping[treatment.name];
    const url =
        "/treatments/" + treatment.name.toLowerCase().replace(/ /g, "-");

    return (
        <TrackedLink
            href={url}
            eventName="treatment_card_clicked"
            eventProperties={{ treatmentName: treatment.name }}
            className="rounded-2xl block transition-all duration-300 ease-out hover:translate-y-[-10px] hover:shadow-lg"
        >
            <Card className="overflow-hidden h-full flex flex-col rounded-2xl">
                <div
                    className={cn(
                        "rounded-t-lg flex items-center justify-center p-10 w-full",
                        PillBGMapping[treatment.id] ?? "bg-[#cbd9ee]",
                    )}
                    style={{ height: "200px" }}
                >
                    {PillMapping[treatment.id]?.(120, index === 0)}
                </div>
                <CardContent className="flex flex-col p-6 grow">
                    <div className="mb-5">
                        <div className="mb-3">
                            <span className="text-[#1a5b77] dark:text-blue-300 text-[28px] font-medium font-satoshi leading-[30.80px]">
                                {treatment.name}
                            </span>
                            {!initialQuestionnaireId && (
                                <span className="ml-2 text-[#1a5b77]/70 dark:text-blue-300/70 text-lg font-normal italic font-satoshi leading-tight">
                                    (Coming Soon)
                                </span>
                            )}
                        </div>
                        <p className="text-[#1a5b77]/60 dark:text-blue-300/60 text-base font-normal font-space leading-snug">
                            {treatment.treating_symptoms.join(", ")}
                        </p>
                    </div>

                    <p className="text-[#333333] dark:text-gray-200 text-lg font-light font-space leading-[1.4] mb-8 grow">
                        {treatment.card_description}
                    </p>

                    <CardFooter className="flex items-end justify-between mt-auto pt-4 border-t border-gray-100 dark:border-gray-600 p-0">
                        <div className="flex flex-col">
                            <div className="text-[#1a5b77] dark:text-blue-300 text-3xl font-light font-satoshi uppercase leading-9 tracking-wider opacity-90 mb-2">
                                {treatment.pricing.estimate}
                            </div>
                            <div className="text-[#333333]/50 dark:text-gray-300/50 text-base font-light font-space leading-snug">
                                Estimated for
                            </div>
                            <div className="text-[#333333]/50 dark:text-gray-300/50 text-base font-light font-space leading-snug">
                                {treatment.pricing.prescriptionLength
                                    ? treatment.pricing.prescriptionLength
                                    : "90"}{" "}
                                day supply
                            </div>
                        </div>

                        <Button
                            variant="outline"
                            id={`learn-more-${treatment.name}`}
                            className="w-48 uppercase h-12"
                        >
                            Learn More
                        </Button>
                    </CardFooter>
                </CardContent>
            </Card>
        </TrackedLink>
    );
}

export function MobileTreatmentCard({
    treatment,
    index,
}: {
    treatment: Treatment;
    index: number;
}) {
    const initialQuestionnaireId =
        TreatmentNameToInitialQuestionnaireIDMapping[treatment.name];
    const url =
        "/treatments/" + treatment.name.toLowerCase().replace(/ /g, "-");

    return (
        <TrackedLink
            href={url}
            eventName="treatment_card_clicked"
            eventProperties={{ treatmentName: treatment.name }}
            className="block"
        >
            <Card className="overflow-hidden">
                <div className="flex">
                    <div
                        className={cn(
                            "w-20 flex items-center justify-center rounded-l-lg shrink-0",
                            PillBGMapping[treatment.id] ?? "bg-[#cbd9ee]",
                        )}
                    >
                        <div className="flex items-center justify-center p-2">
                            {PillMapping[treatment.id]?.(48, index === 0)}
                        </div>
                    </div>

                    <CardContent className="flex-1 p-3 flex flex-col gap-2.5">
                        <div className="text-[#1a5b77] dark:text-blue-300 text-lg font-medium font-satoshi leading-[16.80px]">
                            {treatment.name}
                            {!initialQuestionnaireId && (
                                <span className="ml-2 text-[#1a5b77]/70 dark:text-blue-300/70 text-sm font-normal italic font-satoshi leading-tight">
                                    (Coming Soon)
                                </span>
                            )}
                        </div>
                        <div className="text-[#333333] dark:text-gray-200 text-base font-light font-space leading-tight">
                            {treatment.card_description}
                        </div>

                        <div className="mt-4 flex items-end justify-between">
                            <div className="flex flex-col gap-0.5">
                                <div className="opacity-90 text-[#1a5b77] dark:text-blue-300 text-xl font-light font-satoshi uppercase leading-[17.60px] tracking-wide">
                                    {treatment.pricing.estimate}
                                </div>
                                <div className="text-[#666666] dark:text-gray-300/70 text-sm font-light font-space leading-none">
                                    Estimated for{" "}
                                    {treatment.pricing.prescriptionLength
                                        ? treatment.pricing.prescriptionLength
                                        : "90"}{" "}
                                    day supply
                                </div>
                            </div>
                            <ArrowRight className="h-6 w-6 text-[#1a5b77] dark:text-blue-300 mb-1" />
                        </div>
                    </CardContent>
                </div>
            </Card>
        </TrackedLink>
    );
}
